import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Check, Plus, ChevronDown } from 'lucide-react';

/**
 * PillDropdown — this app's actual custom dropdown, extracted from
 * ShotThumbnail.jsx's shot-type picker (a pill button showing the current
 * value, opening a floating popover of preset pills + an optional free-text
 * fallback) so every dropdown in the app shares ONE design instead of some
 * being this and others being a bare native <select> (which ignores
 * authored styling in some browsers and renders white regardless of dark
 * mode -- confirmed live, not just a style mismatch).
 *
 * Portaled to document.body, positioned from the trigger button's real
 * screen rect -- same fix as MainSidebar's context menus and the shot
 * popovers: a dropdown opened from inside a scrollable/overflow-clipped
 * container (a popover, a narrow sidebar list) must escape that container,
 * not be cut off by it.
 *
 * The popover always renders as a single stacked column of full-width rows
 * (like a real <select> menu) and is sized to the trigger's own rendered
 * width -- it used to render the option list as a flex-wrapped grid of
 * small pill badges sitting side by side (looked like a segmented control,
 * not a dropdown) at a width that ignored the trigger entirely
 * ('max-content', or a floor of 220px for the searchable variant) --
 * confirmed live as visibly inconsistent from one PillDropdown to the next.
 */
const PillDropdown = ({
  value,
  options, // string[] or {value, label, icon?}[] -- icon is an optional rendered node (e.g. a small logo) shown before the label, both in the trigger and its option row
  onChange,
  isDarkMode,
  allowCustom = false,
  // Empty-state text for the TRIGGER button only ("Camera", "Style", ...).
  // Was previously double-booked as the custom-input's placeholder too --
  // a real bug once callers started passing a real field name here (the
  // custom input then showed e.g. "Camera" as its own placeholder instead
  // of anything indicating it's a free-text field). customPlaceholder below
  // is the correct, separate prop for that.
  placeholder = 'Select…',
  customPlaceholder = 'Type a custom value…',
  triggerStyle,
  align = 'left', // 'left' | 'right' -- which edge of the trigger the popover aligns to
  direction = 'down', // 'down' | 'up' -- opens below or above the trigger
  // For large option sets (a language list, a project list, ...) an extra
  // search input above the row list -- the row list itself (and its width)
  // is otherwise identical either way.
  searchable = false,
  searchPlaceholder = 'Search…',
  triggerWidth,
  id, // forwarded to the trigger button so <label htmlFor> still targets something real
  disabled = false,
  // Opt-in, defaults false so every existing caller renders byte-identical
  // to before -- a trailing chevron (pushed to the pill's far right
  // regardless of label length) marking the trigger as an actual dropdown,
  // not a plain text field. Added for callers styling PillDropdown as a
  // full-width form-field-shaped pill (StoryboardFloatingControls' Shot
  // grid), where a bare bordered box with a value in it otherwise gives no
  // sign it's clickable at all -- the compact tag-style pills elsewhere in
  // this app don't need it (their content already implies "a choice").
  showChevron = false,
  // Fires whenever this dropdown's own open/closed state changes -- lets a
  // parent that hover-reveals a whole group of PillDropdowns (e.g. a "Shot
  // settings" section shown on hover) know to stay open while a portaled
  // popover is in use, since the popover renders under document.body, not
  // as a DOM descendant of the hover wrapper -- a plain onMouseLeave on that
  // wrapper fires the moment the cursor crosses into the popover, closing
  // the whole section out from under whoever's using it.
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState('');
  // Custom-value entry starts COLLAPSED to a "+ Custom" row -- an
  // always-visible bare text input sitting under the option list read as a
  // stray second input box, not part of the same control (direct feedback:
  // "what is this two input boxes"). Reset whenever the popover closes so
  // it doesn't stay expanded into the next open.
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const c = isDarkMode
    ? { popoverBg: '#1c1c1c', border: '#2a2a2a', text: '#fff', dim: '#888', activeBg: 'rgba(255,255,255,0.1)', activeText: '#fff', focusBorder: '#666', hoverBg: 'rgba(255,255,255,0.06)' }
    : { popoverBg: '#fff', border: '#e0e0e0', text: '#000', dim: '#666', activeBg: 'rgba(0,0,0,0.06)', activeText: '#000', focusBorder: '#999', hoverBg: 'rgba(0,0,0,0.04)' };

  useEffect(() => { onOpenChange?.(open); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- onOpenChange is a callback, not reactive state; including it would re-fire on every parent render

  const normalized = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const current = normalized.find((o) => o.value === value);
  const filtered = searchable && query.trim()
    ? normalized.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : normalized;

  useEffect(() => {
    if (!open) { setCoords(null); return undefined; }
    const update = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const vertical = direction === 'up'
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 };
      const horizontal = align === 'right'
        ? { right: window.innerWidth - rect.right }
        : { left: rect.left };
      // Matches the trigger's own real rendered width (not the triggerWidth
      // prop, which some callers never pass -- the trigger button then
      // auto-sizes to its label, and the popover needs to match THAT actual
      // width). Floored slightly so a very narrow icon-only trigger doesn't
      // produce an unreadably thin menu for longer option labels.
      setCoords({ ...vertical, ...horizontal, width: Math.max(rect.width, 160) });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, align, direction]);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const commit = useCallback((v) => {
    setOpen(false);
    if (v !== value) onChange(v);
  }, [value, onChange]);

  const submitCustom = useCallback((e) => {
    e.preventDefault();
    const v = customDraft.trim();
    if (v) commit(v);
  }, [customDraft, commit]);

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); setCustomDraft(value || ''); setQuery(''); setShowCustomInput(false); setOpen((v) => !v); }}
        style={{
          fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, color: c.text,
          background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
          border: 'none', borderRadius: searchable ? 6 : 999, padding: '6px 12px',
          cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
          display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
          width: triggerWidth, justifyContent: triggerWidth ? 'space-between' : 'flex-start',
          overflow: 'hidden', textOverflow: 'ellipsis',
          ...triggerStyle,
        }}
      >
        {current?.icon && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{current.icon}</span>}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: showChevron ? 1 : undefined, minWidth: 0 }}>
          {current?.label || value || placeholder}
        </span>
        {showChevron && <ChevronDown size={11} strokeWidth={2} style={{ flexShrink: 0, opacity: 0.6 }} />}
      </button>
      {!disabled && open && coords && createPortal(
        <div
          ref={popoverRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: coords.top, bottom: coords.bottom, left: coords.left, right: coords.right, zIndex: 9999,
            width: coords.width, maxWidth: 'calc(100vw - 24px)',
            display: 'flex', flexDirection: 'column', gap: 6,
            padding: 6, borderRadius: 8,
            backgroundColor: c.popoverBg, border: `1px solid ${c.border}`,
            boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.8)' : '0 4px 20px rgba(0,0,0,0.12)',
          }}
        >
          {searchable && (
            // outline:none with no replacement previously removed all focus
            // feedback -- border color now shifts on focus instead, same
            // treatment the custom-value input below gets.
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                fontFamily: 'Inter, sans-serif', fontSize: 11, color: c.text,
                background: 'transparent', border: `1px solid ${c.border}`, borderRadius: 6, padding: '6px 8px', outline: 'none',
                transition: 'border-color 0.12s ease',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = c.focusBorder; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = c.border; }}
            />
          )}
          {/* Single stacked column of full-width rows -- a real dropdown
              menu, not a wrapped grid of adjacent pill badges.
              flexShrink: 0 on every row below is load-bearing, not
              cosmetic: without it, a flex-column child inside a
              height-constrained overflowY:auto container gets SQUISHED to
              fit instead of overflowing+scrolling once a long option list
              (24 lighting terms, 26 cameras, ...) exceeds maxHeight -- every
              row shrinks toward zero height instead of the container
              scrolling, which is exactly the "breaking/compressed" look a
              long list produced before this fix. */}
          <div style={{ overflowY: 'auto', maxHeight: 320, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.length === 0 ? (
              <div style={{ fontSize: 11, color: c.dim, padding: '6px 7px', fontFamily: 'Inter, sans-serif', flexShrink: 0 }}>No matches</div>
            ) : filtered.map((o) => (
              <button
                key={o.value}
                onClick={() => commit(o.value)}
                style={{
                  width: '100%', textAlign: 'left', fontFamily: 'Inter, sans-serif', fontSize: 11.5,
                  lineHeight: 1.4, fontWeight: o.value === value ? 600 : 400,
                  borderRadius: 6, border: 'none', padding: '7px 8px', cursor: 'pointer',
                  background: o.value === value ? c.activeBg : 'transparent',
                  color: o.value === value ? c.activeText : c.text,
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'background-color 0.12s ease',
                }}
                onMouseEnter={(e) => { if (o.value !== value) e.currentTarget.style.background = c.hoverBg; }}
                onMouseLeave={(e) => { if (o.value !== value) e.currentTarget.style.background = 'transparent'; }}
              >
                {o.icon && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{o.icon}</span>}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{o.label}</span>
                {/* Selection marked by more than background + bold alone --
                    a checkmark reads at a glance scanning a long list,
                    where a subtle tint is easy to miss. */}
                {o.value === value && <Check size={12} strokeWidth={2.5} style={{ flexShrink: 0 }} />}
              </button>
            ))}
          </div>
          {allowCustom && (showCustomInput ? (
            <form onSubmit={submitCustom} style={{ display: 'flex' }}>
              <input
                autoFocus
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
                onBlur={(e) => { e.currentTarget.style.borderColor = c.border; if (!customDraft.trim()) setShowCustomInput(false); }}
                onFocus={(e) => { e.currentTarget.style.borderColor = c.focusBorder; }}
                placeholder={customPlaceholder}
                style={{
                  flex: 1, minWidth: 0, fontFamily: 'Inter, sans-serif', fontSize: 11, color: c.text,
                  background: 'transparent', border: `1px solid ${c.border}`, borderRadius: 6, padding: '5px 8px', outline: 'none',
                  transition: 'border-color 0.12s ease',
                }}
              />
            </form>
          ) : (
            // Collapsed to a plain option-row-styled "+" affordance -- an
            // always-visible bare input here read as a stray second input
            // box below the option list, not part of the same control.
            <button
              type="button"
              onClick={() => setShowCustomInput(true)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 6, textAlign: 'left',
                fontFamily: 'Inter, sans-serif', fontSize: 11.5, fontWeight: 400,
                borderRadius: 6, border: 'none', padding: '6px 8px', cursor: 'pointer',
                background: 'transparent', color: c.dim,
                transition: 'background-color 0.12s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = c.hoverBg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Plus size={11} strokeWidth={2.5} /> Custom…
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default PillDropdown;
